/**
 * Manual-trade API client — thin wrapper around /api/manual-trade/*.
 * Zod shapes live in @pfh/shared; we re-export the useful types here
 * so the Terminal components don't need to know about the shared path.
 */

import type {
  ManualExecuteRequest,
  ManualExecuteResponse,
  ManualHedgeOpenRequest,
  ManualHedgeOpenResponse,
  PendingCreateRequest,
  PendingRow,
  ManualCloseRequest,
  ManualModifyRequest,
} from "@pfh/shared";
import { api } from "../api";

export type {
  ManualExecuteCommand,
  ManualExecuteRequest,
  ManualExecuteResponse,
  ManualExecuteAck,
  ManualHedgeOpenRequest,
  ManualHedgeOpenResponse,
  PendingCreateRequest,
  PendingRow,
  ManualCloseRequest,
  ManualModifyRequest,
} from "@pfh/shared";

export function executeManualTrade(
  body: ManualExecuteRequest,
): Promise<ManualExecuteResponse> {
  return api.post<ManualExecuteResponse>("/api/manual-trade/execute", body);
}

export function openHedge(
  body: ManualHedgeOpenRequest,
): Promise<ManualHedgeOpenResponse> {
  return api.post<ManualHedgeOpenResponse>("/api/manual-trade/open-hedge", body);
}

export function createPendingOrder(body: PendingCreateRequest): Promise<PendingRow> {
  return api.post<PendingRow>("/api/manual-trade/pending", body);
}

export function cancelPendingOrder(id: string): Promise<{ ok: true }> {
  return api.del<{ ok: true }>(`/api/manual-trade/pending/${encodeURIComponent(id)}`);
}

export function listPendingOrders(pairSlug: string): Promise<{ pending: PendingRow[] }> {
  return api.get<{ pending: PendingRow[] }>(
    `/api/manual-trade/pending?pair=${encodeURIComponent(pairSlug)}`,
  );
}

export function closePosition(body: ManualCloseRequest): Promise<{ ok: boolean; dispatched?: number; error?: string }> {
  return api.post("/api/manual-trade/close", body);
}

export function closePairPositions(pairId: string): Promise<{ ok: boolean; dispatched?: number; error?: string }> {
  return api.post("/api/manual-trade/close-pair", { pairId });
}

export function modifyPosition(body: ManualModifyRequest): Promise<{ ok: boolean; dispatched?: number; error?: string }> {
  return api.post("/api/manual-trade/modify", body);
}
